<template>
  <a-card :loading="loading">
    <a-tabs default-active-key="1">
      <a-tab-pane key="1" tab="变更日志">
        <!-- 评论输入区域 -->
        <div class="changelog-edit-container">
          <a-avatar :src="$store.state.userInfo.avatar_url" class="changelog-avatar" />
          <div class="changelog-edit-content">
            <!-- 默认状态：占位符 -->
            <div v-show="!isEdit" class="changelog-edit-placeholder" @click="onAddComment">
              <span class="placeholder-text">添加评论...</span>
            </div>
            <!-- 编辑状态：富文本编辑器 -->
            <div v-show="isEdit" class="changelog-edit-form">
              <!-- 回复提示 -->
              <div v-if="replyTo" class="reply-hint">
                <span class="reply-text">回复 {{ replyTo.creator.nickname }}: {{ getReplyPreview(replyTo.after_content) }}</span>
                <a-icon type="close" class="reply-close" @click="cancelReply" />
              </div>
              <a-form-model ref="form" :model="form" :rules="rules">
                <a-form-model-item prop="content">
                  <vditor-editor ref="editor" v-model="form.content" :height="180" @keydown.native="onKeyDown" />
                </a-form-model-item>
              </a-form-model>
              <div class="changelog-edit-actions">
                <a-button type="primary" style="margin-right: 16px" :loading="submitLoading" @click="onSubmit">确定</a-button>
                <a-button @click="onClose">取消</a-button>
              </div>
            </div>
          </div>
        </div>
        <!-- 变更日志列表 -->
        <a-spin :spinning="loading">
          <a-empty v-if="!loading && changelogData.length === 0" description="暂无变更记录" />
          <div v-else class="changelog-list">
            <div v-for="item in changelogData" :key="item.id" class="changelog-item" :data-changelog-id="item.id">
              <a-avatar :src="item.creator.avatar_url" class="changelog-avatar" />
              <div class="changelog-main">
                <div class="changelog-header">
                  <div class="changelog-author">
                    <user-profiles :value="[item.creator.user_id]" />
                    <span class="event-badge" :class="getEventClass(item.event)">
                      {{ getEventText(item.event, item) }}
                    </span>
                    <span v-if="getReplyCount(item.id) > 0" class="reply-count"> {{ getReplyCount(item.id) }} 条回复 </span>
                  </div>
                  <div class="changelog-time">{{ item.created_time }}</div>
                </div>
                <div class="changelog-content">
                  <div v-if="item.reply_id" class="reply-quote">
                    <div class="quote-content" @click.stop="scrollToChangelog(item.reply_id)">
                      <span class="reply-text"> 回复 {{ getReplyTargetName(item.reply_id) }}: {{ getReplyQuoteContent(item.reply_id) }} </span>
                      <a-icon type="link" class="quote-link-icon" />
                    </div>
                  </div>
                  <vditor-preview :content="item.after_content" />
                </div>
                <div class="changelog-actions" @click.stop>
                  <span class="action-btn" @click="onReply(item)"> <a-icon type="message" class="action-icon" /> 回复 </span>
                </div>
              </div>
            </div>
          </div>
        </a-spin>
      </a-tab-pane>
    </a-tabs>
  </a-card>
</template>

<script>
import { extractMarkdownText } from '@/utils/utils';
import { workflowChangelog, workflowComment } from '@/api/project/workflow';

import VditorEditor from '@/components/VditorEditor';
import VditorPreview from '@/components/VditorEditor/preview';

export default {
  components: { VditorPreview, VditorEditor },
  data() {
    return {
      loading: true,
      filterForm: {
        project_id: parseInt(this.$route.params.project_id) || null,
        workflow_id: parseInt(this.$route.query.workflowId) || null,
        page_size: null,
        page_number: null
      },
      changelogData: [],
      isEdit: false,
      submitLoading: false,
      form: {
        content: ''
      },
      rules: {
        content: [{ required: true, message: '内容不能为空', trigger: 'blur' }]
      },
      replyTo: null // 回复目标
    };
  },
  computed: {
    showDelete() {
      return record => {
        return this.$store.state.userInfo.id === record.creator.user_id;
      };
    }
  },
  methods: {
    // 获取工作流变更日志
    async getWorkflowChangelog(projectId, workflowId) {
      this.loading = true;
      try {
        if (projectId != null) {
          this.filterForm.project_id = projectId;
        }
        if (workflowId != null) {
          this.filterForm.workflow_id = workflowId;
        }
        const res = await workflowChangelog(this.filterForm);
        this.changelogData = res.data.items;
      } catch (error) {
        console.error('获取变更日志失败:', error);
      } finally {
        this.loading = false;
      }
    },

    // 获取事件文本
    getEventText(event, item) {
      switch (event) {
        case 'create':
          return '创建流程';
        case 'modify':
          return '修改流程';
        case 'stop':
          return '终止流程';
        case 'comment':
          return '评论';
        case 'next':
          return `「${item.before_step}」通过，流转至「${item.after_step}」`;
        case 'back':
          return `「${item.before_step}」不通过，流转至「${item.after_step}」`;
        case 'review_passed':
          return `「${item.before_step}」审核通过`;
        default:
          return null;
      }
    },

    // 获取事件样式类
    getEventClass(event) {
      const classMap = {
        create: 'event-create',
        modify: 'event-modify',
        stop: 'event-stop',
        comment: 'event-comment',
        next: 'event-next',
        back: 'event-back',
        review_passed: 'event-review'
      };
      return classMap[event] || 'event-default';
    },

    // 获取回复预览内容
    getReplyPreview(content) {
      return extractMarkdownText(content, 30);
    },

    // 打开评论输入框
    onAddComment() {
      this.$refs.form && this.$refs.form.resetFields();
      this.isEdit = true;
      this.replyTo = null;
      this.$nextTick(() => {
        if (this.$refs.editor && this.$refs.editor.$el) {
          const input = this.$refs.editor.$el.querySelector('textarea');
          if (input) input.focus();
        }
      });
    },

    // 回复某条变更记录
    onReply(item) {
      this.replyTo = item;
      // 自动@被回复人
      this.form.content = `[@${item.creator.nickname}](${item.creator.id}) ：`; // 回复时在内容前添加@
      this.isEdit = true;
      this.$nextTick(() => {
        // 滚动到输入框位置
        this.scrollToInput();
        // 聚焦输入框
        setTimeout(() => {
          if (this.$refs.editor && this.$refs.editor.$el) {
            const input = this.$refs.editor.$el.querySelector('textarea');
            if (input) input.focus();
          }
        }, 300);
      });
    },

    // 取消回复
    cancelReply() {
      this.replyTo = null;
      this.form.content = '';
      this.isEdit = false;
    },

    // 提交评论/回复
    async onSubmit() {
      this.$refs.form.validate(async valid => {
        if (valid) {
          this.submitLoading = true;

          try {
            await workflowComment({
              comment: this.form.content,
              operation_type: 'comment',
              project_id: this.filterForm.project_id,
              workflow_id: this.filterForm.workflow_id,
              reply_id: this.replyTo ? this.replyTo.id : null
            });
            this.$message.success('提交成功');
            this.cancelReply();
            await this.getWorkflowChangelog();
          } catch (e) {
            // 错误处理
          } finally {
            this.submitLoading = false;
          }
        }
      });
    },

    // 关闭输入框
    onClose() {
      this.cancelReply();
    },

    // 键盘事件处理（Ctrl+Enter提交）
    onKeyDown(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        this.onSubmit();
      }
    },
    // 获取指定变更的回复数量
    getReplyCount(itemId) {
      return this.changelogData.filter(item => item.reply_id === itemId).length;
    },
    // 获取被回复的变更对象
    getReplyTarget(replyId) {
      return this.changelogData.find(item => item.id === replyId);
    },
    // 获取被回复人的昵称
    getReplyTargetName(replyId) {
      const target = this.getReplyTarget(replyId);
      return target ? target.creator.nickname : '未知用户';
    },
    // 获取被回复内容的预览
    getReplyQuoteContent(replyId) {
      const target = this.getReplyTarget(replyId);
      if (!target) return '内容已删除';
      return this.getReplyPreview(target.after_content);
    },
    // 滚动并高亮到被引用的变更
    scrollToChangelog(itemId) {
      this.$nextTick(() => {
        const el = this.$el.querySelector(`[data-changelog-id="${itemId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(() => {
            el.classList.add('comment-highlight');
            setTimeout(() => el.classList.remove('comment-highlight'), 2000);
          }, 300);
        }
      });
    },
    // 滚动到输入框位置
    scrollToInput() {
      const inputContainer = this.$el.querySelector('.changelog-edit-container');
      if (inputContainer) {
        inputContainer.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }
  }
};
</script>

<style lang="less" scoped>
.changelog-edit-container {
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
  position: relative;
  overflow: visible;
}
.changelog-edit-content {
  flex: 1;
  min-width: 0;
}
.changelog-edit-placeholder {
  min-height: 36px;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    background: #f5f5f5;
    border-color: #d9d9d9;
  }
  .placeholder-text {
    font-size: 14px;
    color: #999;
    font-style: italic;
  }
}
.changelog-edit-form {
  position: relative;
  .changelog-edit-actions {
    margin-top: 12px;
  }
  .reply-hint {
    display: flex;
    align-items: center;
    background-color: #e6f7ff;
    border: 1px solid #91d5ff;
    border-radius: 4px;
    padding: 8px 12px;
    margin-bottom: 12px;
    font-size: 14px;
    color: #1890ff;
    .reply-text {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .reply-close {
      cursor: pointer;
      font-size: 16px;
      color: #999;
      margin-left: 8px;
      &:hover {
        color: #666;
      }
    }
  }
}
:deep(.ant-card-body) {
  padding: 16px;
}

:deep(.ant-tabs-nav .ant-tabs-tab) {
  margin: -16px 32px 0 0;
}

.changelog-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
  background: transparent;
  transition: all 0.2s ease;
  border-radius: 6px;

  &:hover {
    background-color: #f8f9fa;
  }
}

.changelog-avatar {
  margin-right: 12px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  flex-shrink: 0;
}

.changelog-main {
  flex: 1;
  min-width: 0;
}

.changelog-header {
  margin-bottom: 8px;
}

.changelog-author {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.changelog-time {
  font-size: 13px;
  color: #b0b0b0;
  white-space: nowrap;
  margin-left: auto;
}

.changelog-content {
  font-size: 15px;
  color: #222;
  word-break: break-word;
  line-height: 1.8;
}

.event-badge {
  font-size: 12px;
  padding: 2px 4px;
  border-radius: 8px;
  white-space: nowrap;

  &.event-create {
    color: #52c41a;
    background-color: #f6ffed;
  }

  &.event-modify {
    color: #1890ff;
    background-color: #e6f7ff;
  }

  &.event-stop {
    color: #ff4d4f;
    background-color: #fff2f0;
  }

  &.event-comment {
    color: #722ed1;
    background-color: #f9f0ff;
  }

  &.event-next {
    color: #13c2c2;
    background-color: #e6fffb;
  }

  &.event-back {
    color: #fa8c16;
    background-color: #fff7e6;
  }

  &.event-review {
    color: #52c41a;
    background-color: #f6ffed;
  }

  &.event-default {
    color: #666;
    background-color: #f5f5f5;
  }
}

.changelog-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  font-size: 14px;
  color: #b0b0b0;
  user-select: none;
}

.action-btn {
  display: flex;
  align-items: center;
  cursor: pointer;
  &:hover {
    color: #2176ff;
  }
}

.action-icon {
  font-style: normal;
  margin-right: 4px;
}

.changelog-edit-container,
.changelog-list {
  max-width: 960px;
}
.reply-count {
  color: #1890ff;
  font-size: 12px;
  background-color: #e6f7ff;
  padding: 2px 6px;
  border-radius: 8px;
}
.reply-quote {
  margin-bottom: 12px;
  .quote-content {
    background-color: #f8f9fa;
    border-left: 4px solid #e9ecef;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 14px;
    color: #6c757d;
    line-height: 1.5;
    cursor: pointer;
    transition: all 0.2s ease;
    &:hover {
      background-color: #e9ecef;
      border-left-color: #1890ff;
    }
    .reply-text {
      font-size: 14px;
      color: #6c757d;
      line-height: 1.5;
    }
    .quote-link-icon {
      margin-left: 8px;
      color: #1890ff;
      font-size: 12px;
      cursor: pointer;
    }
  }
}
// 评论高亮效果
.comment-highlight {
  animation: highlight-pulse 2s ease-in-out;
}
@keyframes highlight-pulse {
  0%,
  100% {
    background-color: transparent;
  }
  50% {
    background-color: #fff3cd;
  }
}
</style>
